import dayjs from 'dayjs';
import { DATE_FORMATS, FILTER_TYPE } from '@constants/generic-constants';
import { debounce } from 'lodash';
import { formatCurrency, formatNumbers, isNumericString } from '@utils/utils';

export const filterFieldFormat = (string) => {
  return string?.replaceAll('%20', ' ');
};

export const convertQueryObjToString = (obj, encode = false) => {
  let keys = Object.keys(obj);
  if (keys?.length) {
    return keys
      .map((key) =>
        typeof obj[key] !== 'number' && obj[key]?.indexOf(',') !== -1
          ? obj[key]
            ?.split(',')
            .map((e) => key + '=' + e)
            .join('&')
          : obj[key]
            ? key + '=' + (encode ? encodeURI(obj[key]) : obj[key])
            : ''
      )
      ?.filter((item) => !!item)
      ?.join('&');
  } else {
    return '';
  }
};

export const mapQueryStringToFilterObject = (search, cb = () => { }, setFilterChips) => {
  const queryObj = {},
    queryString = search?.split('?')[1];
  if (queryString) {
    const items = queryString.split('&');
    for (let item of items) {
      if (item.split('=')[0] !== 'widget_for') {
        const key = item.split('=')[0],
          value = item.split('=')[1];
        if (key && value) {
          if (queryObj[key]) {
            queryObj[key] += ',' + filterFieldFormat(value);
          } else {
            queryObj[key] = filterFieldFormat(value);
          }
        }
      }
    }
    for (let key in queryObj) {
      cb(key, queryObj[key]);
    }
  } else {
    !!setFilterChips && setFilterChips(null);
  }
  return { queryObj };
};

export const getQueryString = (filters, noStart) => {
  let queryString = '';
  if (filters) {
    queryString = `${noStart ? '' : '?'}` + convertQueryObjToString(filters, true);
  }
  return queryString;
};

export const removeChildFilter = (baseList, parentSearchKey, obj, parentValue, setSlugAsKey) => {
  const index = baseList?.findIndex((e) => e?.key === parentSearchKey);

  // to remove subKey of a filter - A subKey is an added key that needs to be sent along with the original filter key
  if (baseList[index]?.subKey) {
    delete obj[baseList[index]?.subKey];
  }

  if (!baseList[index]?.child || !baseList[index]?.child?.length) return;

  baseList[index].child.forEach((childFilterKey) => {
    if (childFilterKey) {
      const childFiltersToDelete = baseList
        ?.find((e) => e.key === childFilterKey)
        ?.list?.filter((e) => e.parentId && !e.header && e.parentId.toString() === parentValue.toString());

      childFiltersToDelete &&
        childFiltersToDelete.forEach((e) => {
          removeChildFilter(baseList, childFilterKey, obj, setSlugAsKey ? e?.slug : e?.id, setSlugAsKey);
          removeFilter(obj, childFilterKey, setSlugAsKey ? e?.slug : e?.id);
        });
    }
  });
};

export const removeFilter = (obj, searchKey, value) => {
  if (obj[searchKey]) {
    const array = obj[searchKey].split(',');
    const index = array.indexOf(value.toString());
    if (index > -1) {
      if (array.length > 1) {
        array.splice(index, 1);
        obj[searchKey] = array.join(',');
      } else {
        delete obj[searchKey];
      }
    }
  }
};

const filterKeyPairs = [['f[from_date]', 'f[to_date]']];

export const removeFilterOnDemand = (obj, searchKey) => {
  if (obj[searchKey]) {
    filterKeyPairs.forEach(([key1, key2]) => {
      if (searchKey.includes(key1) || searchKey.includes(key2)) {
        delete obj[`${key1}[gte]`];
        delete obj[`${key2}[lte]`];
      }
    });
    if (searchKey.includes('[lte]')) {
      delete obj[searchKey];
      delete obj[searchKey.replace('[lte]', '[gte]')];
    } else if (searchKey.includes('[gte]')) {
      delete obj[searchKey];
      delete obj[searchKey.replace('[gte]', '[lte]')];
    }
    if (obj.hasOwnProperty(searchKey)) {
      delete obj[searchKey];
    }
  }
};

const getChipObject = ({ chipInfo, key, value, currency }) => {
  const {
    type = '',
    displayName = '',
    options = [],
    mode = 'multiple',
    setAsSlug = false,
    isPriceFilter = false,
    isNumberFilter = false,
  } = chipInfo[key];
  if (!!isPriceFilter) {
    value = formatCurrency({ price: value, currency });
  }
  if (!!isNumberFilter) {
    value = formatNumbers(value);
  }
  switch (type) {
    case 'hidden':
    case 'input':
      return { chipValue: value, chipName: displayName, seperator: ', ' };
    case 'date':
      return {
        chipValue: dayjs(value).format(DATE_FORMATS.monthDateYear),
        chipName: displayName,
        seperator: ' - ',
      };
    case 'range':
      return {
        chipValue: value,
        chipName: displayName,
        seperator: ' - ',
      };
    case 'infinite-dropdown':
      let infiniteDropdownValue = value;
      let chipStr = '';
      if (mode === 'single') {
        if (isNumericString(infiniteDropdownValue)) infiniteDropdownValue = parseInt(infiniteDropdownValue);
        const chipObject = options?.find((item) =>
          setAsSlug ? item?.slug === infiniteDropdownValue : item?.id === infiniteDropdownValue
        );
        chipStr = chipObject?.name ?? chipObject?.title ?? '';
      } else {
        infiniteDropdownValue = value.split(',');
        if (isNumericString(infiniteDropdownValue[0]))
          infiniteDropdownValue = infiniteDropdownValue.map((x) => parseInt(x));
        const chipArray = options?.filter((item) => infiniteDropdownValue.includes(setAsSlug ? item?.slug : item?.id));
        chipStr = chipArray.map((chipObject) => chipObject.name ?? chipObject.title ?? '').join(', ');
      }
      return {
        chipValue: chipStr,
        chipName: displayName,
        seperator: ', ',
      };
    case 'dropdown':
      let dropdownValue = value;
      if (isNumericString(value)) dropdownValue = parseInt(value);
      return {
        chipValue:
          mode === 'multiple'
            ? options
              ?.filter((item) => value?.split(',')?.includes(setAsSlug ? item?.slug : String(item?.id)))
              ?.map((item) => item?.name)
              .join(', ')
            : options?.find((item) => (setAsSlug ? item?.slug === dropdownValue : item?.id === dropdownValue))?.name,
        chipName: displayName,
        seperator: ', ',
      };
    case 'boolean-range':
      return {
        chipValue: key.includes('[gt]')
          ? options.find((option) => option.id === 'true')?.name
          : options.find((option) => option.id === 'false')?.name,
        chipName: displayName,
      };
    case 'tree-select':
      const treeValueIds = value?.split(',')?.map((id) => (isNumericString(id) ? parseInt(id) : id));
      const findSelectedOptions = (options, ids) => {
        let selected = [];
        const searchInTree = (nodes) => {
          if (!nodes) return;
          for (const node of nodes) {
            if (ids.includes(node.value) || ids.includes(node.key)) {
              selected.push(node.title);
            }
            if (node.children) {
              searchInTree(node.children);
            }
          }
        };
        searchInTree(options);
        return selected;
      };
      const selectedTitles = findSelectedOptions(options, treeValueIds);
      return {
        chipValue: selectedTitles.join(', '),
        chipName: displayName,
        seperator: ', ',
      };
    case FILTER_TYPE.TOGGLE:
      const onValue = chipInfo[key]?.onValue || 'true';
      const offValue = chipInfo[key]?.offValue;
      return {
        chipValue: value === onValue ? 'Yes' : (value === offValue ? 'No' : value),
        chipName: displayName,
        seperator: ', ',
      };
    default:
      return '';
  }
};

const getChipInfo = (filtersList) => {
  const chipInfo = {};
  filtersList?.map((filter) => {
    if (filter.type === 'date' || filter.type === 'range' || filter.type === 'boolean-range') {
      if (filter?.keys) {
        chipInfo[`f[${filter?.keys[0]}][gte]`] = {
          type: filter?.type,
          displayName: filter?.chipDisplayName,
          isPriceFilter: filter?.isPriceFilter,
          isNumberFilter: filter?.isNumberFilter,
        };
        chipInfo[`f[${filter?.keys[1]}][lte]`] = {
          type: filter?.type,
          displayName: filter?.chipDisplayName,
        };
      } else {
        chipInfo[`f[${filter?.key}][gt${filter.type !== 'boolean-range' ? 'e' : ''}]`] = {
          type: filter?.type,
          displayName: filter?.chipDisplayName,
          isPriceFilter: filter?.isPriceFilter,
          isNumberFilter: filter?.isNumberFilter,
          ...(filter.type === 'boolean-range' && { options: filter?.options }),
        };
        chipInfo[`f[${filter?.key}][lte]`] = {
          type: filter?.type,
          displayName: filter?.chipDisplayName,
          isPriceFilter: filter?.isPriceFilter,
          isNumberFilter: filter?.isNumberFilter,
          ...(filter.type === 'boolean-range' && { options: filter?.options }),
        };
      }
    } else if (filter.type === 'cascading-dropdown') {
      // Handle parent
      if (filter?.parent?.key) {
        chipInfo[filter.parent.key] = {
          type: 'infinite-dropdown',
          displayName: filter.parent.chipDisplayName || filter.chipDisplayName,
          options: filter.parent.options || [],
          mode: filter.parent.mode || 'single',
          setAsSlug: filter.parent.setAsSlug,
        };
      }
      // Handle child
      if (filter?.child?.key) {
        chipInfo[filter.child.key] = {
          type: 'dropdown',
          displayName: filter.child.chipDisplayName || filter.chipDisplayName,
          options: filter.child.options || [],
          mode: filter.child.mode || 'single',
          setAsSlug: filter.child.setAsSlug,
        };
      }
    } else {
      chipInfo[filter?.key] = {
        type: filter?.type,
        displayName: filter?.chipDisplayName,
        options: filter?.nested ? filter?.options?.flatMap((item) => item?.options) : filter?.options,
        mode: filter?.mode,
        setAsSlug: filter?.setAsSlug,
        onValue: filter?.onValue,
        offValue: filter?.offValue,
      };
    }
  });
  return chipInfo;
};

export const handleFilterChips = ({ location, filtersList, currency }) => {
  const chipInfo = getChipInfo(filtersList);
  const filterCh = [];
  const obj = {};
  const chipMap = {};

  const CallbacksObj = {
    price: (a, b) => {
      const val1 = parseInt(a.replace(currency, '').trim());
      const val2 = parseInt(b.replace(currency, '').trim());
      return val1 - val2;
    },
    date: (a, b) => {
      const val1 = dayjs(a, DATE_FORMATS.dayMonthYearFilters).toDate();
      const val2 = dayjs(b, DATE_FORMATS.dayMonthYearFilters).toDate();
      return val1 - val2;
    },
    default: (a, b) => {
      const val1 = parseInt(a);
      const val2 = parseInt(b);
      return val1 - val2;
    },
  };

  const sortRangeValues = (values, slug) => {
    const slugsList = ['price', 'date'];
    const formattedSlug = slugsList?.includes(slug) ? slug : 'default';
    return values.sort(CallbacksObj?.[formattedSlug]);
  };

  mapQueryStringToFilterObject(location.search, (key, value) => {
    if (!chipInfo[key]) return;
    const chipObject = getChipObject({ chipInfo, key, value, currency });

    if (!obj[chipObject.chipName]) {
      chipMap[chipObject.chipName] = key;
      obj[chipObject.chipName] = `${chipObject.chipValue}`;
    } else {
      const existingValues = obj[chipObject.chipName].split(chipObject.seperator).map((val) => val.trim());
      const newValue = `${chipObject.chipValue}`;

      existingValues.push(newValue);

      const sortedValues = sortRangeValues(existingValues, chipInfo[key]?.type);
      obj[chipObject.chipName] = sortedValues.join(chipObject.seperator);
    }
  });
  Object.keys(obj).forEach((element) => {
    const space = '\n';
    if (obj[element]) {
      filterCh.push({
        title: (
          <>
            <span style={{ color: 'var(--gray-500)' }}>
              {element}:{space}
            </span>
            <span style={{ fontWeight: 'var(--ant-font-weight-strong)' }}>{decodeURIComponent(obj[element])}</span>
          </>
        ),
        searchKey: chipMap[element],
        searchValue: obj[element],
        location,
      });
    }
  });
  return filterCh;
};

export const getFiltersArray = (filtersList) => {
  const advancedFilters = [];
  const filters = [];
  filtersList.forEach((filter) => {
    if (filter.isAdvancedFilter) {
      advancedFilters.push(filter);
    } else {
      filters.push(filter);
    }
  });

  return { filters, advancedFilters };
};

export const infiniteScrollApiCall = async ({ api = async () => { }, query = '', key = '', page = 1 }) => {
  const data = await api(query ? `?page=${page}&${query}` : `?page=${page}`);
  if (data?.[key]) {
    return {
      list: data[key],
      current_page: data?.pagination?.current_page,
      next_page: data?.pagination?.next_page,
    };
  } else {
    return { list: [], current_page: null, next_page: null };
  }
};

export const fetchSearchDebounce = debounce(async ({ api = async () => { }, query = '' }) => {
  api({ page: 1, query });
}, 500);

export const findQuickLink = ({ location, quickLinks }) => {
  if (!location?.search || !quickLinks?.length > 0) return null;

  return quickLinks.find((link) => link.link === `${location?.pathname}${location?.search}`);
};

export const showResetButton = (location) => {
  // Counts the unique keys from location.search
  const { queryObj } = mapQueryStringToFilterObject(location.search);
  const keys = [];

  Object.keys(queryObj).forEach((key) => {
    key.split('[').map((item) => {
      if (!item.includes('lte') && !item.includes('gte')) {
        keys.push(item);
      }
    });
  });
  const uniqueKeys = new Set(keys.filter((item) => item.length > 1));

  return uniqueKeys.size > 0;
};
